import { KR_HOLIDAYS_2026, KR_HOLIDAYS_2027, type CalendarDefinition } from "@/engine/calendar";
import { toDay, weekdayOfDay, type ISODate } from "@/engine/date";
import { eachHolidayDate, holidayCatalog, holidayDateConflict, resolveCalendar, type UserHoliday } from "@/engine/holiday-catalogs";
import { formatDate, formatDateRange, formatMonthDaySpan, weekdayNames } from "@/lib/date-format";

/** index 0 = Sunday, matching CalendarDefinition.workingDays. Names come from the shared date locale. */
export const WEEKDAY_LABELS: readonly string[] = weekdayNames("short");
export const WEEKDAY_LONG_LABELS: readonly string[] = weekdayNames("long");
/** display order: Monday first */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** 자주 쓰는 주. 칩으로 하루를 더 고치면 어느 프리셋과도 맞지 않는다. */
export const WORKWEEK_PRESETS: ReadonlyArray<{ id: string; label: string; days: CalendarDefinition["workingDays"] }> = [
  { id: "5", label: "주 5일", days: [false, true, true, true, true, true, false] },
  { id: "6", label: "주 6일", days: [false, true, true, true, true, true, true] },
  { id: "7", label: "매일", days: [true, true, true, true, true, true, true] },
];

export function matchWorkweekPreset(workingDays: CalendarDefinition["workingDays"]): string | null {
  const hit = WORKWEEK_PRESETS.find((preset) => preset.days.every((on, index) => on === workingDays[index]));
  return hit?.id ?? null;
}

const KR_HOLIDAYS_BY_YEAR: Record<number, readonly ISODate[]> = {
  2026: KR_HOLIDAYS_2026,
  2027: KR_HOLIDAYS_2027,
};

/** 플랜이 걸친 해 가운데 한국 공휴일 목록이 있는 해만. */
export function krHolidaysForYears(years: Iterable<number>): ISODate[] {
  return [...years].sort((a, b) => a - b).flatMap((year) => KR_HOLIDAYS_BY_YEAR[year] ?? []);
}

/** "주 5일 · 월–금", "주 3일 · 월·수·금", "주 6일 · 월–토" */
export function describeWorkweek(workingDays: CalendarDefinition["workingDays"]): string {
  const runs: number[][] = [];
  WEEK_ORDER.forEach((d, pos) => {
    if (!workingDays[d]) return;
    if (pos > 0 && workingDays[WEEK_ORDER[pos - 1]!] && runs.length) runs.at(-1)!.push(d);
    else runs.push([d]);
  });
  const count = runs.reduce((n, r) => n + r.length, 0);
  if (count === 7) return "주 7일 · 매일";
  const parts = runs.map((r) => (r.length >= 3 ? `${WEEKDAY_LABELS[r[0]!]}–${WEEKDAY_LABELS[r.at(-1)!]}` : r.map((d) => WEEKDAY_LABELS[d]).join("·")));
  return `주 ${count}일 · ${parts.join("·")}`;
}

/** Turn one weekday on/off. Returns null when that would leave no working day. */
export function toggleWorkday(workingDays: CalendarDefinition["workingDays"], day: number): CalendarDefinition["workingDays"] | null {
  const next = [...workingDays] as CalendarDefinition["workingDays"];
  next[day] = !next[day];
  return next.some(Boolean) ? next : null;
}

export interface HolidayItem {
  date: ISODate;
  weekday: number;
  /** already a non-working weekday, so the holiday changes nothing */
  onRestDay: boolean;
}

export type HolidayOrigin = "catalog" | "user";

export interface HolidayRow {
  key: string;
  origin: HolidayOrigin;
  catalogId?: string;
  userId?: string;
  /** false = 사용 안 함. The row stays; the schedule ignores it. */
  enabled: boolean;
  /** every day in the run is already a non-working weekday */
  onRestDay: boolean;
  items: HolidayItem[];
  /** "02월 17일 (화)" or "02월 16일 ~ 18일" */
  label: string;
  name: string;
  note?: string;
  /** "대한민국 공휴일" or "추가" */
  sourceLabel: string;
}

export interface HolidayYear {
  year: number;
  items: HolidayItem[];
  /** how many of them actually remove a working day */
  effective: number;
  rows: HolidayRow[];
}

const RUN_SUFFIX = " 연휴";

function holidayStem(name: string): string {
  return name.endsWith(RUN_SUFFIX) ? name.slice(0, -RUN_SUFFIX.length) : name;
}

function dayItem(date: ISODate, workingDays: CalendarDefinition["workingDays"]): HolidayItem {
  const weekday = weekdayOfDay(toDay(date));
  return { date, weekday, onRestDay: !workingDays[weekday] };
}

function catalogRows(def: CalendarDefinition): HolidayRow[] {
  const suppressed = new Set((def.holidaySuppressions ?? []).map((item) => `${item.catalogId}:${item.date}`));
  const rows: HolidayRow[] = [];
  for (const id of def.holidayCatalogs ?? []) {
    const catalog = holidayCatalog(id);
    if (!catalog) continue;
    for (const day of [...catalog.days].sort((a, b) => a.date.localeCompare(b.date))) {
      const enabled = !suppressed.has(`${id}:${day.date}`);
      const item = dayItem(day.date, def.workingDays);
      const prev = rows.at(-1);
      const prevLast = prev?.items.at(-1);
      const consecutive = !!prev && prev.catalogId === id && !!prevLast && toDay(day.date) === toDay(prevLast.date) + 1;
      const same = !!prev && prev.enabled === enabled && prev.onRestDay === item.onRestDay && holidayStem(prev.name) === holidayStem(day.name);
      if (prev && consecutive && same) {
        prev.items.push(item);
        prev.label = formatHolidaySpan(prev.items);
        prev.name = holidayStem(day.name);
        prev.key = `${id}:${prev.items[0]!.date}:${day.date}`;
        continue;
      }
      rows.push({
        key: `${id}:${day.date}`,
        origin: "catalog",
        catalogId: id,
        enabled,
        onRestDay: item.onRestDay,
        items: [item],
        label: fmtHoliday(item),
        name: day.name,
        sourceLabel: catalog.label,
      });
    }
  }
  return rows;
}

function userRows(def: CalendarDefinition): HolidayRow[] {
  return (def.userHolidays ?? []).map((item) => {
    const dates = eachHolidayDate(item.start, item.end);
    const items = dates.map((date) => dayItem(date, def.workingDays));
    const onRestDay = items.length > 0 && items.every((entry) => entry.onRestDay);
    return {
      key: item.id,
      origin: "user" as const,
      userId: item.id,
      enabled: item.enabled,
      onRestDay,
      items,
      label: items.length === 1 ? fmtHoliday(items[0]!) : formatHolidaySpan(items),
      name: item.name,
      note: item.note,
      sourceLabel: "추가",
    };
  });
}

export function groupHolidaysByYear(def: CalendarDefinition): HolidayYear[] {
  const resolved = resolveCalendar(def);
  const rows = [...catalogRows(resolved), ...userRows(resolved)].sort((a, b) => a.items[0]!.date.localeCompare(b.items[0]!.date) || (a.origin === b.origin ? 0 : a.origin === "catalog" ? -1 : 1));
  const byYear = new Map<number, HolidayRow[]>();
  for (const row of rows) {
    const year = Number(row.items[0]!.date.slice(0, 4));
    const list = byYear.get(year) ?? [];
    list.push(row);
    byYear.set(year, list);
  }
  return [...byYear.entries()]
    .sort(([a], [b]) => a - b)
    .map(([year, yearRows]) => {
      const items = yearRows.flatMap((row) => row.items);
      return { year, items, effective: yearRows.flatMap((row) => (row.enabled ? row.items.filter((item) => !item.onRestDay) : [])).length, rows: yearRows };
    });
}

export function toggleHolidayCatalog(def: CalendarDefinition, id: string, on: boolean): CalendarDefinition {
  const base = resolveCalendar(def);
  const selected = new Set(base.holidayCatalogs ?? []);
  if (on) selected.add(id);
  else selected.delete(id);
  return resolveCalendar({ ...base, holidayCatalogs: [...selected] });
}

export function setHolidayRowEnabled(def: CalendarDefinition, row: Pick<HolidayRow, "origin" | "catalogId" | "userId" | "items">, enabled: boolean): CalendarDefinition {
  const base = resolveCalendar(def);
  if (row.origin === "user" && row.userId) {
    return resolveCalendar({
      ...base,
      userHolidays: (base.userHolidays ?? []).map((item) => (item.id === row.userId ? { ...item, enabled } : item)),
    });
  }
  const dates = new Set(row.items.map((item) => item.date));
  const catalogId = row.catalogId ?? "";
  const kept = (base.holidaySuppressions ?? []).filter((item) => item.catalogId !== catalogId || !dates.has(item.date));
  const holidaySuppressions = enabled ? kept : [...kept, ...[...dates].map((date) => ({ catalogId, date }))];
  return resolveCalendar({ ...base, holidaySuppressions });
}

export function addPlanHoliday(def: CalendarDefinition, input: { date: ISODate; name: string; note?: string }): CalendarDefinition {
  const base = resolveCalendar(def);
  const note = input.note?.trim();
  const item: UserHoliday = {
    id: `uh-${input.date}-${crypto.randomUUID().slice(0, 8)}`,
    start: input.date,
    end: input.date,
    name: input.name.trim(),
    ...(note ? { note } : {}),
    enabled: true,
  };
  return resolveCalendar({ ...base, userHolidays: [...(base.userHolidays ?? []), item] });
}

export function updatePlanHoliday(def: CalendarDefinition, id: string, patch: { name: string; note: string }): CalendarDefinition {
  const base = resolveCalendar(def);
  const note = patch.note.trim();
  return resolveCalendar({
    ...base,
    userHolidays: (base.userHolidays ?? []).map((item) => (item.id === id ? { ...item, name: patch.name.trim() || item.name, note: note || undefined } : item)),
  });
}

export function removePlanHoliday(def: CalendarDefinition, id: string): CalendarDefinition {
  const base = resolveCalendar(def);
  return resolveCalendar({ ...base, userHolidays: (base.userHolidays ?? []).filter((item) => item.id !== id) });
}

/** Official days in the year stay as 사용 안 함. Added holidays in the year are removed. */
export function clearHolidayYear(def: CalendarDefinition, year: number): CalendarDefinition {
  const base = resolveCalendar(def);
  const prefix = `${year}-`;
  const existing = new Set((base.holidaySuppressions ?? []).map((item) => `${item.catalogId}:${item.date}`));
  const extra = (base.holidayCatalogs ?? []).flatMap((id) => {
    const catalog = holidayCatalog(id);
    if (!catalog) return [];
    return catalog.days.filter((day) => day.date.startsWith(prefix) && !existing.has(`${id}:${day.date}`)).map((day) => ({ catalogId: id, date: day.date }));
  });
  return resolveCalendar({
    ...base,
    holidaySuppressions: [...(base.holidaySuppressions ?? []), ...extra],
    userHolidays: (base.userHolidays ?? []).filter((item) => !item.start.startsWith(prefix)),
  });
}

export { holidayDateConflict };

/** Years the schedule spans; those groups start expanded. */
export function yearsBetween(start: ISODate, finish: ISODate): Set<number> {
  const a = Number(start.slice(0, 4));
  const b = Math.max(a, Number(finish.slice(0, 4)));
  const out = new Set<number>();
  for (let y = a; y <= b; y++) out.add(y);
  return out;
}

/** "10월 03일 (토)", shown under its year group. */
export function fmtHoliday(item: HolidayItem): string {
  return formatDate(item.date, "monthDayWeekday");
}

/** "2026년 10월 03일 (토)", for labels read outside the year group (aria, confirm text). */
export function fmtHolidayFull(item: HolidayItem): string {
  return formatDate(item.date, "fullWeekday");
}

/** One day keeps the weekday. A run is the full-date range. */
export function fmtHolidayRun(items: HolidayItem[]): string {
  if (items.length === 1) return fmtHolidayFull(items[0]!);
  return formatDateRange(items[0]!.date, items.at(-1)!.date, "full");
}

function formatHolidaySpan(items: HolidayItem[]): string {
  if (items.length === 1) return fmtHoliday(items[0]!);
  return formatMonthDaySpan(items[0]!.date, items.at(-1)!.date);
}

/** Year group heading, e.g. "2026년". */
export function fmtHolidayYear(year: number): string {
  return formatDate(`${year}-01-01`, "year");
}
